<template>
  <div
    class="nav-mobile-subdrop-container"
    :class="{ 'nav-mobile-subdrop--open': open }"
  >
    <button
      class="nav-mobile-subdrop__back"
      @click="handleSubdropClose"
    >
      <SvgIconArrowMenu /> {{ backButtonText[locale] }}
    </button>

    <div class="nav-mobile-subdrop">
      <div
        v-if="withLinkOverview"
        class="nav-mobile-subdrop__overview"
      >
        <T3Link
          :to="rootLink.link"
          class="nav-mobile-subdrop__overview-header"
        >
          <span @click="handleLinkClick">
            <T3HtmlParser :content="rootLink?.header" /> <SvgIconLinkArrow />
          </span>
        </T3Link>

        <T3HtmlParser
          :content="rootLink.description"
          class="nav-mobile-subdrop__overview-description"
          tag="span"
        />
      </div>

      <ul class="nav-mobile-subdrop__list">
        <li
          v-for="(link, index) in rootLink.children"
          :key="index"
        >
          <template v-if="link.children?.length">
            <component
              :is="link.spacer ? 'div' : 't3-link'"
              :to="link.spacer ? null : link.link"
              class="nav-mobile-subdrop__sublist-title"
            >
              <T3HtmlParser :content="link.title" />
              <SvgIconLinkArrow v-if="!link.spacer" />
            </component>

            <ul class="nav-mobile-subdrop__sublist">
              <li
                v-for="(sublink, subindex) in link.children"
                :key="subindex"
              >
                <T3Link
                  :to="getLink(sublink)"
                  class="nav-mobile-subdrop__link"
                >
                  <span @click="handleLinkClick">
                    {{ sublink.title }}
                  </span>
                </T3Link>
              </li>
            </ul>
          </template>

          <template v-else>
            <T3Link
              :to="getLink(link)"
              class="nav-mobile-subdrop__link"
            >
              <span @click="handleLinkClick">
                {{ link.title }}
              </span>
            </T3Link>
          </template>
        </li>
      </ul>

      <div
        v-if="withLinkMedia"
        class="nav-mobile-subdrop__media-wrapper"
      >
        <T3Link
          class="nav-mobile-subdrop__media"
          :to="linkMedia?.properties?.linkData"
        >
          <UiImg
            :image="linkMedia"
            :width="linkMedia?.properties.dimensions.width"
            :height="linkMedia?.properties.dimensions.height"
            loading="lazy"
            fetchpriority="low"
          />
        </T3Link>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { T3Link } from '@t3headless/nuxt-typo3'
import { computed } from 'vue'

import SvgIconArrowMenu from '~ui/assets/icons/icon-arrow-menu.svg'
import SvgIconLinkArrow from '~ui/assets/icons/icon-link-arrow.svg'
import type { INavigation, INavigationWithMedia } from '~ui/types'

const backButtonText = {
  en: 'back',
  pl: 'wstecz',
  de: 'zurück'
} as const

interface Props {
  rootLink: INavigation | INavigationWithMedia
  locale: 'en' | 'pl' | 'de'
  open: boolean
}

const props = defineProps<Props>()

const withLinkMedia = computed(() => {
  if (!('media' in props.rootLink)) return
  return !!props.rootLink.media?.length
})

const linkMedia = computed(() => {
  if (!('media' in props.rootLink)) return
  return props.rootLink.media?.[1] ?? props.rootLink.media?.[0]
})

const withLinkOverview = computed(() => {
  return props.rootLink?.header && props.rootLink?.description && !withLinkMedia.value
})

const getLink = (link: INavigation): T3Link => {
  return { href: link.link, target: link.target }
}

const emit = defineEmits<{
  (event: 'link-click'): void
  (event: 'close', index?: number): void
}>()

const handleLinkClick = () => emit('link-click')
const handleSubdropClose = () => emit('close')
</script>

<style lang="scss">
.nav-mobile-subdrop {
  &-container {
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: auto 1fr;
    position: absolute;
    inset: 0;
    top: var(--subdrop-offset);
    z-index: z-index(nav);
    background-color: #faf9fa;
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    transform: translateX(100%);
    transition:
      opacity 0.4s,
      visibility 0.4s,
      transform 0.3s ease;
  }

  &--open {
    opacity: 1;
    visibility: visible;
    pointer-events: all;
    transform: translateX(0);
  }

  &__back {
    display: flex;
    align-items: center;
    width: 100%;
    height: 40px;
    background-color: color(primary);
    border: none;
    outline: none;
    color: color(white);
    font-family: inherit;
    font-weight: 500;
    text-transform: uppercase;
    text-align: left;
    padding-right: rem($container-padding);
    padding-left: rem($container-padding - 9px);

    & svg {
      rotate: 90deg;

      & path {
        fill: currentColor;
      }
    }
  }

  display: flex;
  flex-direction: column;
  gap: rem(32px);
  height: 100%;
  width: 100%;
  overflow-y: auto;

  &__overview {
    color: color(grey-1000);
    padding-inline: rem($container-padding);

    &:first-child {
      padding-top: rem($container-padding);
    }

    &-header > span {
      display: inline-flex;
      align-items: center;
      gap: rem(8px);
      font-size: rem(32px);
      font-weight: 600;
      margin-bottom: rem(16px);

      & svg {
        height: 32px;
        width: 32px;

        & path {
          transition: fill 0.2s;
        }
      }

      &:is(:hover, :active, :focus) svg path {
        fill: currentColor;
      }
    }

    &-description {
      color: color(grey-450);
      font-weight: 400;

      & > *:last-child {
        margin-bottom: 0;
      }
    }
  }

  &__list,
  &__sublist {
    padding: 0;
    margin: 0;
    display: grid;
  }

  &__list {
    gap: rem(40px);
    padding: rem($container-padding);
    padding-block: 0px;

    &:first-child {
      padding-top: rem($container-padding);
    }
  }

  &__sublist {
    gap: rem(20px);
  }

  &__sublist-title {
    display: inline-flex;
    align-items: center;
    color: color(grey-450);
    font-size: 14px;
    margin-bottom: rem(24px);

    &:is(:hover, :active, :focus) svg path {
      fill: currentColor;
    }

    & svg {
      height: 22px;
      width: 22px;

      & path {
        transition: fill 0.2s;
        fill: color(grey-450);
      }
    }
  }

  &__media {
    &-wrapper {
      height: 100%;
      background-color: color(white);
      border-top: 1px solid color(grey-200);
      padding: rem($container-padding);
    }

    img {
      display: block;
      width: 100%;
      border-radius: em(6px);
      border: 1px solid color(grey-200);
    }
  }

  &__link {
    color: color(grey-800);
    font-weight: 600;
    font-size: rem(18px);

    &.router-link-exact-active {
      text-decoration: 2px underline color(primary);
      text-underline-offset: 6px;
    }
  }
}
</style>
